/**
 * Permission 权限模块接口
 * 调用 wildwood-cms 后端获取用户权限
 */

import axios from 'axios';
import { WILDWOOD_CMS_HOST } from '../request/config';

// ============ 类型定义 ============

/**
 * 用户权限信息
 */
export interface UserPermission {
  user_name: string;
  is_admin: number;
  roleKeys?: string[];
  routerKeys: string[];
  actionKeys: string[];
}

/**
 * 权限 API 响应
 */
interface PermissionResponse {
  success: boolean;
  data: UserPermission | null;
}

// ============ 配置 ============

const APP_KEY = 'tmall_audience';

// ============ API 方法 ============

/**
 * 获取 Okta access token
 */
const getAccessToken = (): string => {
  try {
    return localStorage.getItem('access_token') || '';
  } catch {
    return '';
  }
};

/**
 * 获取当前用户权限
 * 注意：不需要传 operator header，后端 oktaAuthMiddleware 会自动从 JWT token 中解析用户信息
 * @returns 用户权限信息
 */
export const getUserPermissions = async (): Promise<UserPermission | null> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.warn('No access token found');
      return null;
    }

    const response = await axios.get<PermissionResponse>(
      `${WILDWOOD_CMS_HOST}/access/user/permissions`,
      {
        params: { app_key: APP_KEY },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch user permissions:', error);
    return null;
  }
};

// ============ 权限检查工具函数 ============

/**
 * 检查用户是否有某个路由权限
 */
export const hasRouterAccess = (permissions: UserPermission | null, routerKey: string): boolean => {
  if (!permissions) return false;
  if (permissions.is_admin === 1) return true;
  return permissions.routerKeys.includes(routerKey);
};

/**
 * 检查用户是否有某个操作权限
 */
export const hasActionAccess = (permissions: UserPermission | null, actionKey: string): boolean => {
  if (!permissions) return false;
  if (permissions.is_admin === 1) return true;
  return permissions.actionKeys.includes(actionKey);
};

/**
 * 检查用户是否是管理员
 */
export const isAdmin = (permissions: UserPermission | null): boolean => {
  return permissions?.is_admin === 1;
};

// ============ 路由权限常量 ============
// 与 SQL: tmall_config_access_routers 表中的 router_key 对应
// 统一为一个权限，所有页面查看共用

export const ROUTER_KEYS = {
  PAGE_VIEW: 'page_view', // 所有页面查看权限
} as const;

// ============ 操作权限常量 ============
// 与 SQL: tmall_config_access_actions 表中的 action_key 对应

export const ACTION_KEYS = {
  AUDIENCE_CREATE: 'audience:create', // 创建人群
  AUDIENCE_EXPORT: 'audience:export', // 导出人群数据
} as const;
